import axios from "axios"
import { useTimeoutFn } from "@vueuse/core"

const POLLING_FREQUENCY = 60 * 1000
const API_PATH = "/exchange/member/playerService/testLine"

const fetchLine = async (line: string, currentAPIUrl: string) => {
  const baseURL = currentAPIUrl.replace(/^[^.]*/, line)
  const fullURL = `${window.location.protocol}//${baseURL}${API_PATH}`

  try {
    const response = await axios({
      baseURL: `${window.location.protocol}//${baseURL}`,
      url: API_PATH,
      method: "post",
      withCredentials: true,
      headers: {
        source: 1
      }
    })

    console.debug(`Line test success: ${line} -> ${baseURL}`, response.status)
    return baseURL
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error)
    console.warn(`Line test failed: ${line} -> ${fullURL}`, {
      error: errorMessage,
      line,
      baseURL
    })

    throw new Error(`Line ${line} test failed: ${errorMessage}`)
  }
}

/**
 * 根據提供的線路和當前 API URL，嘗試取得第一條成功的線路。
 *
 * @function
 * @async
 * @param {string[]} lines - 要測試的線路。
 * @param {string} currentAPIUrl - 當前的API URL。
 * @returns {Promise<string>} promise 成功，解析第一个成功的 baseURL；否則，如果没有成功，則拒绝。
 * @throws 如果没有線路成功，將抛出錯誤。
 *
 * @example
 * const lines = ['aaa', 'bbb'];
 * const currentAPIUrl = 'https://example.com.api';
 * 依照順序發送請求 1. https://aaa.com.api/exchange/member/playerService/testLine
 *               2. https://bbb.com.api/exchange/member/playerService/testLine
 * 取得第一個返回的請求，可能是 aaa 或 bbb 線路
 *
 */
const fetchTestLines = async (
  lines: string[],
  currentAPIUrl: string
): Promise<string> => {
  if (lines.length === 0) {
    throw new Error("No available lines for testing")
  }

  console.debug(`Starting line test: [${lines.join(", ")}]`)

  try {
    const result = await Promise.any(lines.map(line => fetchLine(line, currentAPIUrl)))
    console.debug(`Line test completed, using line: ${result}`)
    return result
  } catch (error) {
    const errorMessage =
      error instanceof AggregateError
        ? `All line tests failed: ${error.errors.map(e => e.message).join(", ")}`
        : `Line test failed: ${error instanceof Error ? error.message : String(error)}`

    console.error(errorMessage)
    throw new Error(errorMessage)
  }
}

const extractSubdomain = (domain: string) => {
  const match = domain.match(/^([^.]+)\./)
  return match ? match[1] : null
}

type UseChangeLineCoreParams = {
  webSiteDomain: Ref<string>
  lines: Ref<string[]>
  prevChangeLine: Ref<string | undefined>
  isEnableChangeLine: Ref<boolean>
  currentAPIUrl: Ref<string>
  shouldLoadJBLScript: Ref<boolean>
  hasMountedJBL: Ref<boolean>
  updateChangeLine: (line: string) => void
  updateBaseUrl: (url: string) => void
  loadJBLScript: () => Promise<void>
  initJBLWebSocket: (domain: string) => Promise<void>
  fetchRecommendLine: () => Promise<string>
}

const useChangeLineCore = ({
  webSiteDomain,
  lines,
  prevChangeLine,
  isEnableChangeLine,
  currentAPIUrl,
  updateChangeLine,
  updateBaseUrl,
  shouldLoadJBLScript,
  hasMountedJBL,
  loadJBLScript,
  initJBLWebSocket,
  fetchRecommendLine
}: UseChangeLineCoreParams) => {
  const getChangeLine = async (): Promise<string> => {
    if (!shouldLoadJBLScript.value) {
      return fetchTestLines(lines.value, currentAPIUrl.value)
    }

    if (!hasMountedJBL.value) {
      await loadJBLScript()
      await initJBLWebSocket(webSiteDomain.value)
    }

    const recommendLine = await fetchRecommendLine()
    if (recommendLine === "") {
      return fetchTestLines(lines.value, currentAPIUrl.value)
    }

    if (recommendLine === prevChangeLine.value) {
      return recommendLine
    }

    try {
      const subdomain = extractSubdomain(recommendLine)
      if (!subdomain) throw new Error("[3.1]:invalid subdomain")

      return await fetchTestLines([subdomain], currentAPIUrl.value)
    } catch {
      return fetchTestLines(lines.value, currentAPIUrl.value)
    }
  }

  const changeLineAsync = async () => {
    try {
      const changeLine = await getChangeLine()

      if (prevChangeLine.value !== changeLine) {
        updateBaseUrl(changeLine)
        updateChangeLine(changeLine)
      }
    } catch (error) {
      console.warn(error)
    }
  }

  const { start, stop } = useTimeoutFn(
    () => {
      changeLineAsync()
      start()
    },
    POLLING_FREQUENCY,
    { immediate: false }
  )

  /*
   * 四個條件都要成立才會開始換線
   * 1. UAT or SIM
   * 2. 有線路資料
   * 3. 開啟換線功能
   * 4. 線路資料有變動
   */
  watch(
    () => [lines.value, isEnableChangeLine.value],
    (newValue, prevValue) => {
      const [newLines, newIsEnableChangeLine] = newValue
      const [prevLines] = prevValue || []

      const isProduction = import.meta.env.PROD
      const hasLines = Object.keys(newLines).length > 0
      const hasDifferentLines = JSON.stringify(prevLines) !== JSON.stringify(newLines)

      if (isProduction && hasLines && newIsEnableChangeLine && hasDifferentLines) {
        changeLineAsync()
        start()
      }
    },
    {
      immediate: true
    }
  )

  onUnmounted(() => {
    stop()
  })
}

export default useChangeLineCore
